import React, { useState } from 'react'
import { Tabs, Tab, useDisclosure } from '@heroui/react'

import {
  type BatchConversionResult,
  type BatchLoopRepairResult,
} from '../services/mediaManagementService'

import Mp4Management from './Mp4Management'
import GifManagement from './GifManagement'
import LoopAnalysis from './LoopAnalysis'
import UrlAnalysis from './UrlAnalysis'
import VideoMetadata from './VideoMetadata'
import FileSizeMigration from './FileSizeMigration'
import ProgressModal from './ProgressModal'
import { MediaOverviewStatsCard } from './MediaOverviewStats'
import MediaMatchingTable from './MediaMatchingTable'

// Simple icon components
const AnalysisIcon = () => <span>🔍</span>
const LoopIcon = () => <span>🔁</span>
const Mp4Icon = () => <span>🎬</span>

// Types
interface ConversionProgress {
  isRunning: boolean
  currentBatch: string[]
  results: BatchConversionResult | null
  error: string | null
}

interface LoopRepairProgress {
  isRunning: boolean
  currentBatch: string[]
  results: BatchLoopRepairResult | null
  error: string | null
}

export const MediaManagement: React.FC = () => {
  // Tab management
  const [activeTab, setActiveTab] = useState<
    | 'mp4'
    | 'loop'
    | 'url-analysis'
    | 'video-metadata'
    | 'matching-table'
    | 'migrations'
  >('mp4')

  // Progress states
  const [conversionProgress, setConversionProgress] =
    useState<ConversionProgress>({
      isRunning: false,
      currentBatch: [],
      results: null,
      error: null,
    })
  const [loopRepairProgress, setLoopRepairProgress] =
    useState<LoopRepairProgress>({
      isRunning: false,
      currentBatch: [],
      results: null,
      error: null,
    })

  // Modal controls
  const {
    isOpen: isProgressModalOpen,
    onOpen: onProgressModalOpen,
    onClose: onProgressModalClose,
  } = useDisclosure()
  const [progressModalContent, setProgressModalContent] = useState<
    'conversion' | 'repair' | null
  >(null)
  const [conversionTitle, setConversionTitle] = useState<string | undefined>(
    undefined
  )

  // Progress handlers
  const handleShowProgress = (
    type: 'conversion' | 'repair',
    progress: ConversionProgress | LoopRepairProgress
  ) => {
    if (type === 'conversion') {
      setConversionProgress(progress as ConversionProgress)
      // Title will be set by the caller before invoking this handler
    } else {
      setLoopRepairProgress(progress as LoopRepairProgress)
    }
    setProgressModalContent(type)
    onProgressModalOpen()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            Media Management
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            Manage GIF files, convert missing MP4 counterparts, and repair loop
            extensions
          </p>
        </div>
      </div>

      {/* Overview Stats Card */}
      <MediaOverviewStatsCard />

      <Tabs
        selectedKey={activeTab}
        onSelectionChange={(key) =>
          setActiveTab(
            key as
              | 'mp4'
              | 'loop'
              | 'url-analysis'
              | 'video-metadata'
              | 'matching-table'
              | 'migrations'
          )
        }
        className="w-full"
        variant="underlined"
        size="lg"
      >
        <Tab
          key="mp4"
          title={
            <div className="flex items-center space-x-2">
              <Mp4Icon />
              <span>MP4 Management</span>
            </div>
          }
        >
          <Mp4Management
            onShowProgress={(type, progress) => {
              setConversionTitle('Converting GIFs to MP4')
              handleShowProgress(type, progress)
            }}
          />
        </Tab>

        <Tab
          key="gif"
          title={
            <div className="flex items-center space-x-2">
              <span>🖼️</span>
              <span>GIF Management</span>
            </div>
          }
        >
          <GifManagement
            onShowProgress={(type, progress) => {
              setConversionTitle('Converting MP4s to GIF')
              handleShowProgress(type, progress)
            }}
          />
        </Tab>

        <Tab
          key="loop"
          title={
            <div className="flex items-center space-x-2">
              <LoopIcon />
              <span>Loop Analysis</span>
            </div>
          }
        >
          <LoopAnalysis onShowProgress={handleShowProgress} />
        </Tab>

        <Tab
          key="url-analysis"
          title={
            <div className="flex items-center space-x-2">
              <AnalysisIcon />
              <span>URL Analysis</span>
            </div>
          }
        >
          <UrlAnalysis />
        </Tab>

        <Tab
          key="video-metadata"
          title={
            <div className="flex items-center space-x-2">
              <span>📐</span>
              <span>Video Metadata</span>
            </div>
          }
        >
          <VideoMetadata />
        </Tab>

        <Tab
          key="matching-table"
          title={
            <div className="flex items-center space-x-2">
              <span>🔗</span>
              <span>Matching Table</span>
            </div>
          }
        >
          <MediaMatchingTable />
        </Tab>

        <Tab
          key="migrations"
          title={
            <div className="flex items-center space-x-2">
              <span>⚙️</span>
              <span>Migrations</span>
            </div>
          }
        >
          <FileSizeMigration />
        </Tab>
      </Tabs>

      <ProgressModal
        isOpen={isProgressModalOpen}
        onClose={onProgressModalClose}
        type={progressModalContent}
        conversionProgress={conversionProgress}
        loopRepairProgress={loopRepairProgress}
        conversionTitle={conversionTitle}
      />
    </div>
  )
}

export default MediaManagement
